import axios from '../utils/http';

const BASE_URL = '/api/users';
const BASE_SECURITY_URL = '/api/security';
const LONG_TASK_TIMEOUT_MS = 20 * 60 * 1000;
const LOGIN_STORAGE_KEYS = ['username', 'avatar', 'role', 'userID', 'token', 'loginExpiry'];

const buildGuestAdminCapabilities = () => ({
  code: 200,
  data: {
    userID: null,
    isGlobalAdmin: false,
    managedMatchIDs: [],
    permissions: {
      canManageUser: false,
      canManageBans: false,
      canManageCleanup: false,
      canManageSimulate: false,
      canManagePlayers: false,
      canManagePoints: false,
      canManageHistoryOps: false,
      canManageServerMode: false,
      canManageMysqlReplConfig: false,
      canManageTeamTemplates: false,
    },
  },
});

const clearLocalLoginState = () => {
  if (typeof window === 'undefined') return;
  try {
    LOGIN_STORAGE_KEYS.forEach((key) => window.localStorage?.removeItem(key));
  } catch (_) {}
};

const hasActiveLocalLogin = () => {
  if (typeof window === 'undefined') return true;
  try {
    const username = String(window.localStorage?.getItem('username') || '').trim();
    if (!username) return false;
    const expiry = Number(window.localStorage?.getItem('loginExpiry'));
    if (!Number.isFinite(expiry) || expiry <= 0) return false;
    return Date.now() <= expiry;
  } catch (_) {
    return false;
  }
};

// 根据用户ID返回用户role, 不要异步的方法
export const fetchUserRole = async (id) => {
  try {
    const response = await axios.get(`${BASE_URL}/getrole/${id}`);
    return response.data;
  } catch (error) {
    console.error('获取用户角色失败:', error);
    throw error;
  }
};

// 获取当前登录用户的后台管理能力（仅信任后端 req.user）
export const fetchMyAdminCapabilities = async () => {
  if (!hasActiveLocalLogin()) {
    clearLocalLoginState();
    return buildGuestAdminCapabilities();
  }

  try {
    const response = await axios.get(`${BASE_URL}/me/adminCapabilities`);
    return response.data;
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    if (status === 401 || status === 403) {
      clearLocalLoginState();
      return buildGuestAdminCapabilities();
    }
    console.error('获取当前用户管理能力失败');
    throw error;
  }
};

// AccessGuard：由后端统一校验当前用户是否可访问指定角色页面 / 能力页面
export const checkMyAccess = async ({
  allowedRoles = [],
  matchID = null,
  allowAnyMatchRole = false,
  requiredCapabilities = [],
  requireAllCapabilities = false,
  routeKey = '',
} = {}) => {
  try {
    const payload = {
      allowedRoles: Array.isArray(allowedRoles) ? allowedRoles : [],
      matchID,
      allowAnyMatchRole: Boolean(allowAnyMatchRole),
      requiredCapabilities: Array.isArray(requiredCapabilities) ? requiredCapabilities : [],
      requireAllCapabilities: Boolean(requireAllCapabilities),
      routeKey: String(routeKey || '').trim(),
    };
    const response = await axios.post(`${BASE_URL}/me/access-check`, payload);
    return response.data;
  } catch (error) {
    console.error('后端访问权限校验失败:', error);
    throw error;
  }
};

// 获取所有用户
export const fetchAllUsers = async (userID) => {
  try {
    const path = userID ? `${BASE_URL}/all/${userID}` : `${BASE_URL}/all`;
    const response = await axios.get(path);
    return response.data;
  } catch (error) {
    console.error('获取所有用户失败!!!!!', error);
    throw error;
  }
};

// 获取特殊用户（角色不是 user 和 player）
export const fetchSpecialUsers = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/special`);
    return response.data;
  } catch (error) {
    console.error('获取特殊用户失败:', error);
    throw error;
  }
};

// 获取角色申请用户
export const fetchRoleApplications = async (userID) => {
  try {
    const path = userID ? `${BASE_URL}/applications/${userID}` : `${BASE_URL}/applications/me`;
    const response = await axios.get(path);
    // const response = await axios.get(`${BASE_URL}/applications`);
    return response.data;
  } catch (error) {
    console.error('获取角色申请失败:', error);
    throw error;
  }
};

// 更新用户信息
export const updateUserInfo = async (user) => {
  try {
    const response = await axios.put(`${BASE_URL}/update`, user);
    return response.data;
  } catch (error) {
    console.error('更新用户信息失败:', error);
    throw error;
  }
};

// 账号设置：获取个人资料
export const fetchSelfProfile = async (userID) => {
  try {
    const path = userID ? `${BASE_URL}/profile/${userID}` : `${BASE_URL}/profile/me`;
    const response = await axios.get(path);
    return response.data;
  } catch (error) {
    console.error('获取个人资料失败:', error);
    throw error;
  }
};

// 账号设置：更新昵称、邮箱
export const updateSelfProfile = async ({ userID, username, email }) => {
  try {
    const payload = { username, email };
    if (userID != null) payload.userID = userID;
    const response = await axios.put(`${BASE_URL}/profile`, payload);
    return response.data;
  } catch (error) {
    console.error('更新个人资料失败:', error);
    throw error;
  }
};

// 账号设置：修改密码
export const changeSelfPassword = async ({ userID, oldPassword, newPassword }) => {
  try {
    const payload = { oldPassword, newPassword };
    if (userID != null) payload.userID = userID;
    const response = await axios.put(`${BASE_URL}/profile/password`, payload);
    return response.data;
  } catch (error) {
    console.error('修改密码失败:', error);
    throw error;
  }
};

// 用户注册
export const registerUser = async (user) => {
  try {
    console.error('用户注册:', user);
    const response = await axios.post(`${BASE_URL}/register`, user);
    return response.data;
  } catch (error) {
    console.error('用户注册失败:', error);
    throw error;
  }
};


// 用户登陆（已调整为仅支持“手机号 + 密码”）
// 期望入参示例：
//   loginUser({ phone: '13800000000', password: '***' })
export const loginUser = async (user) => {
  try {
    // 这里不做字段名转换，由调用方（如 FooterNav.js 的 handleOk）保证传入 { phone, password }
    // 若仍传入 username，将无法通过后端校验
    const response = await axios.post(`${BASE_URL}/login`, user);
    console.log('用户登陆成功:', response.data);
    return response.data;
  } catch (error) {
    console.error('用户登陆失败:', error);
    throw error;
  }
};

// 用户退出：清理后端 auth_uid Cookie
export const logoutUser = async () => {
  try {
    const response = await axios.post(`${BASE_URL}/logout`, {});
    return response.data;
  } catch (error) {
    console.error('用户退出失败:', error);
    throw error;
  }
};


//进入某场比赛重置用户角色
export const resetRole = async (matchID, userid) => {
  try {
    // 我需要给后端api resetMatchRole传递两个参数，一个是比赛id，一个是用户id
    const response = await axios.post(`${BASE_URL}/resetMatchRole`, {matchID, userid});
    return response.data;
  } catch (error) {
    console.error('重置用户角色失败:', error);
    throw error;
  }
};

// 管理员：标记用户的“密码需重置”状态（将 refresh_password 置为 1）
export const markPasswordReset = async (userID) => {
  try {
    // 语义化：使用 PUT，并把 userID 放在路径中
    const response = await axios.put(`${BASE_URL}/resetPasswordFlag/${userID}`);
    return response.data;
  } catch (error) {
    console.error('标记用户密码需重置失败:', error);
    throw error;
  }
};

// 查看用户角色申请情况
export const fetchRoleApplicationByUserID = async (userID) => {
  try {
    const path = userID ? `${BASE_URL}/roleApplication/${userID}` : `${BASE_URL}/roleApplication/me`;
    const response = await axios.get(path);
    return response.data;
  } catch (error) {
    console.error('获取用户角色申请失败:', error);
    throw error;
  }
};

// 提交用户角色申请
export const submitRoleApplication = async (application) => {
  try {
    const response = await axios.post(`${BASE_URL}/applyRole`, application);
    return response.data;
  } catch (error) {
    console.error('提交用户角色申请失败:', error);
    throw error;
  }
};

// 审批用户角色申请
export const updateUserRole = async (application, approved) => {
  try {
    application.approved = approved;
    console.log('审批用户角色申请:', application);
    const response = await axios.put(`${BASE_URL}/updateUserRole`, application);
    return response.data;
  } catch (error) {
    console.error('审批用户角色申请失败:', error);
    throw error;
  }
};

// 用户角色查询
export const fetchRoleByUserID = async (userID) => {
  try {
    const path = userID ? `${BASE_URL}/roles/${userID}` : `${BASE_URL}/roles/me`;
    const response = await axios.get(path);
    return response.data;
  } catch (error) {
    console.error('获取用户角色失败:', error);
    throw error;
  }
};

// 撤销申请
export const revokeApplication = async (matchID, userID) => {
  try {
    const payload = { matchID };
    if (userID != null) payload.userID = userID;
    const response = await axios.delete(`${BASE_URL}/cancelApply`, { data: payload });
    return response.data;
  } catch (error) {
    console.error('撤销申请失败:', error);
    throw error;
  }
};

// 全局管理员：预览某赛事可清理的申请权限（不含 admin）
export const fetchMatchRoleCleanupPreview = async (matchID) => {
  try {
    const response = await axios.get(`${BASE_URL}/roleCleanup/preview/${matchID}`);
    return response.data;
  } catch (error) {
    console.error('获取权限清理预览失败:', error);
    throw error;
  }
};

// 全局管理员：一键清理某赛事申请权限（保留 admin）
export const executeMatchRoleCleanup = async ({ matchID, confirmText }) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/roleCleanup/execute`,
      { matchID, confirmText },
      { timeout: LONG_TASK_TIMEOUT_MS }
    );
    return response.data;
  } catch (error) {
    console.error('执行权限清理失败:', error);
    throw error;
  }
};

// 管理员：清理指定赛事的所有关联数据（高危操作）
export const cleanupMatchData = async ({ matchID, stage, confirmText }) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/cleanupMatch`,
      { matchID, stage, confirmText },
      { timeout: LONG_TASK_TIMEOUT_MS }
    );
    return response.data;
  } catch (error) {
    console.error('赛事清理失败:', error);
    throw error;
  }
};

// 管理员：执行赛事模拟测试脚本（高危操作）
export const runMatchSimulationTest = async (payload = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/simulateMatch`,
      payload,
      { timeout: LONG_TASK_TIMEOUT_MS }
    );
    return response.data;
  } catch (error) {
    console.error('赛事测试执行失败:', error);
    throw error;
  }
};

// 全局管理员：获取服务器模式
export const fetchServerModeAdmin = async () => {
  try {
    const response = await axios.get(`${BASE_SECURITY_URL}/server-mode`);
    return response.data;
  } catch (error) {
    console.error('获取服务器模式失败:', error);
    throw error;
  }
};

// 全局管理员：获取访问级别（AUTO / NORMAL / DEGRADED / EMERGENCY）
export const fetchAccessLevelAdmin = async () => {
  try {
    const response = await axios.get(`${BASE_SECURITY_URL}/access-level`);
    return response.data;
  } catch (error) {
    console.error('获取访问级别失败:', error);
    throw error;
  }
};

// 全局管理员：获取 MySQL 主从配置状态
export const fetchMysqlReplicationConfigAdmin = async () => {
  try {
    const response = await axios.get(`${BASE_SECURITY_URL}/mysql-repl-config`);
    return response.data;
  } catch (error) {
    console.error('获取 MySQL 主从配置失败:', error);
    throw error;
  }
};

// 全局管理员：切换服务器模式
export const switchServerModeAdmin = async ({ mode, confirmText }) => {
  try {
    const response = await axios.post(`${BASE_SECURITY_URL}/server-mode`, {
      mode,
      confirmText,
    });
    return response.data;
  } catch (error) {
    console.error('切换服务器模式失败:', error);
    throw error;
  }
};

// 全局管理员：提交 MySQL 主从配置切换任务
export const switchMysqlReplicationConfigAdmin = async ({ mode, confirmText }) => {
  try {
    const response = await axios.post(`${BASE_SECURITY_URL}/mysql-repl-config`, {
      mode,
      confirmText,
    });
    return response.data;
  } catch (error) {
    console.error('提交 MySQL 主从配置切换任务失败:', error);
    throw error;
  }
};

// 全局管理员：手动设置访问级别
export const switchAccessLevelAdmin = async ({ level, confirmText }) => {
  try {
    const response = await axios.post(`${BASE_SECURITY_URL}/access-level`, {
      level,
      confirmText,
    });
    return response.data;
  } catch (error) {
    console.error('切换访问级别失败:', error);
    throw error;
  }
};

// 管理员：Game_History 备份到 badminton_history
export const backupGameHistoryAdmin = async () => {
  try {
    const response = await axios.post('/api/gameHistory/admin/backup', {});
    return response.data;
  } catch (error) {
    console.error('Game_History 备份失败:', error);
    throw error;
  }
};

// 管理员：清空主库 Game_History
export const truncateGameHistoryAdmin = async ({ confirmText }) => {
  try {
    const response = await axios.post('/api/gameHistory/admin/truncate', { confirmText });
    return response.data;
  } catch (error) {
    console.error('Game_History 清空失败:', error);
    throw error;
  }
};

// 管理员：先备份再重载 Game_History（支持 fromDate）
export const backupReloadGameHistoryAdmin = async ({ confirmText, fromDate }) => {
  try {
    const response = await axios.post('/api/gameHistory/admin/backup-reload', {
      confirmText,
      fromDate,
    });
    return response.data;
  } catch (error) {
    console.error('Game_History 备份并重载失败:', error);
    throw error;
  }
};
